import { type FunctionComponent, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { MenuBase, NavigationBar } from '@retailer-platform/shared-components/src/tds'
import {
  NotificationsDropdown,
  useUnreadNotificationsWithToast,
} from '@retailer-platform/domain-notifications'
import { ICPlatformVersion } from '../../../utils/ic-platform/versioning/versions.constants'
import useAccessControl from '../../../legacy/components/AccessControl/useAccessControl'
import { type AccessControlConfig } from '../../../legacy/components/AccessControl/accessControl.utils'
import { type FeatureToggle } from '../../../legacy/contexts/featureToggles/FeatureToggleContext'
import { useTopBarMenu } from './TopBarMenuContext'

const UNREAD_COUNT_POLLING_INTERVAL = 30000 // 30 seconds

const NotificationsAccessControlConfig: AccessControlConfig = {
  featureToggles: ['rt_notification_center' as FeatureToggle],
  minICPlatformVersion: ICPlatformVersion.r2025_07_0,
}

export const TdsNotificationsButton: FunctionComponent = () => {
  const intl = useIntl()
  const { isOpen, setIsOpen } = useTopBarMenu('notifications')
  const hasAccess = useAccessControl()

  const {
    totalUnreadCount: unreadCount,
    refetch: refetchUnreadCount,
    onDrawerOpen,
  } = useUnreadNotificationsWithToast({
    pollingInterval: UNREAD_COUNT_POLLING_INTERVAL,
  })

  const handleStateChange = useCallback(
    (next: boolean) => {
      setIsOpen(next)
      if (next) {
        onDrawerOpen()
      } else {
        refetchUnreadCount()
      }
    },
    [setIsOpen, onDrawerOpen, refetchUnreadCount]
  )

  const onClose = useCallback(() => handleStateChange(false), [handleStateChange])

  if (!hasAccess(NotificationsAccessControlConfig)) {
    return null
  }

  const trigger = (
    <span css={{ position: 'relative', display: 'inline-flex' }}>
      <NavigationBar.Button
        aria-label={intl.formatMessage({ id: 'app.nav.notificationsMenu.label' })}
        icon="notifications"
        onPress={() => handleStateChange(!isOpen)}
      />
      {unreadCount > 0 && (
        <span
          aria-hidden
          css={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#FF3B30',
            pointerEvents: 'none',
          }}
        />
      )}
    </span>
  )

  return (
    <MenuBase
      target={trigger}
      isOpen={isOpen}
      onMenuStateChange={handleStateChange}
      position="bottomEnd"
    >
      <NotificationsDropdown isOpen={isOpen} onClose={onClose} />
    </MenuBase>
  )
}
